import React, { useEffect, useRef, useState } from 'react';
import { useShopifyCookies } from '@shopify/hydrogen-react';
import { updateShopifyAnalyticsConsent } from '../utils/ShopifyAnalytics';

const TrackingCookies = ({ onReady }) => {
  const ready = useShopifyCookies({
    hasUserConsent: true,
    checkoutDomain: process.env.GATSBY_SHOPIFY_CHECKOUT_DOMAIN,
    storefrontAccessToken: process.env.GATSBY_STOREFRONT_ACCESS_TOKEN,
    fetchTrackingValues: true,
    ignoreDeprecatedCookies: true,
  });

  useEffect(() => {
    if (ready) onReady();
  }, [ready, onReady]);

  return null;
};

const ShopifyConsent = ({ children }) => {
  const lastSubmittedConsent = useRef(null);

  const [consent, setConsent] = useState({
    resolved: false,
    analyticsAllowed: false,
  });

  useEffect(() => {
    let active = true;

    const syncConsent = () => {
      const cookiebot = window.Cookiebot;
      const customerPrivacy = window.Shopify?.customerPrivacy;

      if (!cookiebot || !customerPrivacy) return;

      /*
       * Shopify says consent should only be recorded following a user
       * decision. An existing saved Cookiebot response also qualifies.
       */
      if (!cookiebot.hasResponse) return;

      const nextConsent = {
        analytics: Boolean(cookiebot.consent.statistics),
        marketing: Boolean(cookiebot.consent.marketing),
        preferences: Boolean(cookiebot.consent.preferences),
      };

      const signature = JSON.stringify(nextConsent);

      if (signature === lastSubmittedConsent.current) return;

      lastSubmittedConsent.current = signature;

      customerPrivacy.setTrackingConsent(
        {
          ...nextConsent,
          headlessStorefront: true,
          checkoutRootDomain: process.env.GATSBY_SHOPIFY_CHECKOUT_DOMAIN,
          storefrontRootDomain: process.env.GATSBY_SHOPIFY_STOREFRONT_DOMAIN,
          storefrontAccessToken: process.env.GATSBY_STOREFRONT_ACCESS_TOKEN,
        },
        () => {
          if (!active) return;

          const analyticsAllowed = customerPrivacy.analyticsProcessingAllowed();

          setConsent({
            resolved: true,
            analyticsAllowed,
          });

          /*
           * A declined choice can be resolved immediately. An accepted
           * choice waits for TrackingCookies below.
           */
          if (!analyticsAllowed) {
            updateShopifyAnalyticsConsent({
              ready: true,
              analyticsAllowed: false,
            });
          }
        },
      );
    };

    const privacyScript = document.getElementById(
      'shopify-customer-privacy-api',
    );

    privacyScript?.addEventListener('load', syncConsent);

    window.addEventListener('CookiebotOnConsentReady', syncConsent);
    window.addEventListener('CookiebotOnAccept', syncConsent);
    window.addEventListener('CookiebotOnDecline', syncConsent);

    /*
     * Covers the case where both scripts finished before React mounted.
     */
    syncConsent();

    return () => {
      active = false;

      privacyScript?.removeEventListener('load', syncConsent);

      window.removeEventListener('CookiebotOnConsentReady', syncConsent);
      window.removeEventListener('CookiebotOnAccept', syncConsent);
      window.removeEventListener('CookiebotOnDecline', syncConsent);
    };
  }, []);

  const trackingReady = () => {
    updateShopifyAnalyticsConsent({
      ready: true,
      analyticsAllowed: true,
    });
  };

  return (
    <>
      {children}

      {consent.resolved && consent.analyticsAllowed && (
        <TrackingCookies onReady={trackingReady} />
      )}
    </>
  );
};

export default ShopifyConsent;
